import { QueryClient, QueryKey } from "@tanstack/react-query";
import { BaseService } from "../services/BaseService";

export class CacheManager {
  private static instance: CacheManager | null = null;
  private queryClient: QueryClient;

  private constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
  }

  static getInstance(queryClient: QueryClient): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager(queryClient);
    }
    return CacheManager.instance;
  }

  refreshQueryData(entityKey: QueryKey) {
    this.queryClient.removeQueries({ queryKey: entityKey, exact: true });
    this.queryClient.invalidateQueries({ queryKey: entityKey });
    this.queryClient.refetchQueries({ queryKey: entityKey, exact: true });
  }

  getCachedDataById<T>(entityKey: QueryKey, id: number): T | undefined {
    const queryKey = Array.isArray(entityKey) ? entityKey : [entityKey];
    return this.queryClient.getQueryData<T>([...queryKey, id]);
  }

  deleteFromCache<T>(entityKey: QueryKey, id: number) {
    const queryKey = Array.isArray(entityKey) ? entityKey : [entityKey];

    // מחיקה מהרשימה הכללית
    this.queryClient.setQueryData<T[]>(queryKey, (oldData) =>
      oldData ? oldData.filter((item) => (item as any).id !== id) : []
    );

    // מחיקה מה-cache לפי ID
    this.queryClient.removeQueries({ queryKey: [...queryKey, id], exact: true });

    // מחיקה מכל ה-field indexes (queries שמתחילים ב-entityKey + "by")
    this.queryClient.setQueriesData<T[]>(
      { queryKey: [...queryKey, "by"], exact: false },
      (oldData) => oldData ? oldData.filter((item) => (item as any).id !== id) : []
    );
  }
  async fetchMissingData<T, K extends keyof T>(
    entityKey: QueryKey,
    ids: number[],
    fetchFn: (ids: number[]) => Promise<T[]>,
    idKey: K
  ): Promise<T[]> {
    const { cached, missing } = this.getCachedDataByIds<T>(entityKey, ids);
  
    if (missing.length === 0) {
      return cached;
    }
  
    const fetchedData = await fetchFn(missing);
  
    fetchedData.forEach((data) => {
      this.updateCache(entityKey, data, idKey);
    });
  
    return [...cached, ...fetchedData];
  }
  getCachedDataByIds<T>(entityKey: QueryKey, ids: number[]): { cached: T[]; missing: number[] } {
    const queryKey = Array.isArray(entityKey) ? entityKey : [entityKey];
  
    const cachedList = this.queryClient.getQueryData<T[]>(queryKey) || [];
  
    const cached: T[] = [];
    const missing: number[] = [];
  
    ids.forEach((id) => {
      const found = cachedList.find((item) => (item as any).id === id);
      if (found) {
        cached.push(found);
      } else {
        missing.push(id);
      }
    });
  
    return { cached, missing };
  }
  
  updateCache<T, K extends keyof T>(
    entityKey: QueryKey,
    data: T,
    idKey: K,
    fieldIndexes?: {
      [F in keyof T]?: (service: BaseService<T>) => (value: T[F]) => Promise<T[]>;
    }
  ) {
    const queryKey = Array.isArray(entityKey) ? entityKey : [entityKey];
    const id = data[idKey] as unknown as number;

    // עדכון המטמון לפי ID (אם יש צורך)
    this.queryClient.setQueryData([...queryKey, id], data);

    // עדכון הרשימה הכללית (אם קיימת)
    this.queryClient.setQueryData<T[]>(queryKey, (oldData) => {
      if (!oldData) return [data];
      const index = oldData.findIndex((item) => (item as any)[idKey] === id);
      if (index === -1) {
        // אם לא נמצא, הוסף בסוף
        return [...oldData, data];
      }
      // אם נמצא, עדכן במקום - יצירת אובייקט חדש לגמרי כדי ש-React Query יזהה שינוי
      const updatedList = [...oldData];
      updatedList[index] = { ...data };
      return updatedList;
    });

    // עדכון לפי שדות נוספים
    Object.entries(fieldIndexes ?? {}).forEach(([fieldName, _]) => {
      const fieldValue = data[fieldName as keyof T];
      if (fieldValue === undefined) return;
      const fieldKey = [...queryKey, "by", fieldName, fieldValue];

      this.queryClient.setQueryData<T[]>(fieldKey, (oldData) => {
        if (!oldData) return [data];
        const updatedList = oldData.filter((item) => (item as any)[idKey] !== id);
        return [...updatedList, data];
      });
    });
  }
}
